import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { toast } from "sonner";
import { Trash2, Upload, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const empty = { title: "", category: "residential", before: null, after: null };

export default function GalleryManager({ token }) {
  const [projects, setProjects] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(empty);

  const authHeader = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchProjects(); /* eslint-disable-next-line */ }, []);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/api/gallery`);
      setProjects(res.data);
    } catch (err) {
      // ignore; list simply stays as-is
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!form.before || !form.after) return toast.error("Please select both a Before and an After photo");
    const fd = new FormData();
    fd.append('title', form.title);
    fd.append('category', form.category);
    fd.append('before', form.before);
    fd.append('after', form.after);
    setUploading(true);
    try {
      await axios.post(`${API}/api/gallery`, fd, {
        headers: { ...authHeader, 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Project added!");
      setForm(empty);
      document.getElementById('before-upload').value = "";
      document.getElementById('after-upload').value = "";
      fetchProjects();
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this project?")) return;
    try {
      await axios.delete(`${API}/api/gallery/${id}`, { headers: authHeader });
      toast.success("Project removed");
      fetchProjects();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-8" data-testid="gallery-manager">
      <Card className="bg-black border-neutral-800 h-fit">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Upload className="h-5 w-5 text-red-600" /> Add Project (Before / After)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpload} className="space-y-4">
            <div>
              <Label className="text-neutral-400">Project Title</Label>
              <Input
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Backyard Patio Transformation"
                className="bg-neutral-900 border-neutral-800 text-white mt-1"
                required
                data-testid="project-title-input"
              />
            </div>
            <div>
              <Label className="text-neutral-400">Category</Label>
              <select
                value={form.category}
                onChange={e => setForm({ ...form, category: e.target.value })}
                className="flex h-10 w-full items-center justify-between rounded-md border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm text-white mt-1"
                data-testid="project-category-select"
              >
                <option value="residential">Residential</option>
                <option value="commercial">Commercial</option>
                <option value="hardscape">Hardscaping</option>
                <option value="maintenance">Maintenance</option>
              </select>
            </div>
            <div>
              <Label className="text-neutral-400">Before Photo</Label>
              <Input
                id="before-upload"
                type="file"
                accept="image/*"
                onChange={e => setForm({ ...form, before: e.target.files[0] })}
                className="bg-neutral-900 border-neutral-800 text-white mt-1"
                required
                data-testid="project-before-input"
              />
            </div>
            <div>
              <Label className="text-neutral-400">After Photo</Label>
              <Input
                id="after-upload"
                type="file"
                accept="image/*"
                onChange={e => setForm({ ...form, after: e.target.files[0] })}
                className="bg-neutral-900 border-neutral-800 text-white mt-1"
                required
                data-testid="project-after-input"
              />
            </div>
            <Button type="submit" data-testid="project-upload-btn" className="w-full bg-red-600 hover:bg-red-700" disabled={uploading}>
              {uploading ? <Loader2 className="animate-spin" /> : "Add Project"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <div className="lg:col-span-2 space-y-4">
        <h2 className="text-xl font-bold mb-4">Projects ({projects.length})</h2>
        {projects.length === 0 ? (
          <p className="text-neutral-500 text-sm">No projects yet. Add one using the form.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {projects.map(p => (
              <div key={p.id} className="bg-neutral-900 border border-neutral-800 rounded-lg p-3" data-testid={`admin-project-${p.id}`}>
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-bold truncate text-white">{p.title}</h3>
                  <Button size="icon" variant="ghost" onClick={() => handleDelete(p.id)} className="text-red-500 hover:text-red-400 hover:bg-red-900/20 h-8 w-8 shrink-0">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <img src={`${API}${p.beforeUrl}`} alt="Before" className="w-full h-24 object-cover rounded bg-neutral-800" />
                    <p className="text-xs text-neutral-500 mt-1 text-center">Before</p>
                  </div>
                  <div>
                    <img src={`${API}${p.afterUrl}`} alt="After" className="w-full h-24 object-cover rounded bg-neutral-800" />
                    <p className="text-xs text-red-400 mt-1 text-center">After</p>
                  </div>
                </div>
                <p className="text-xs text-neutral-500 mt-2 capitalize">{p.category}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
